"use client";

import type React from "react";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { Plus, Minus, Save, ArrowLeft } from "lucide-react";
import { useAppTheme } from "../../ThemeContext.tsx";
import { FlowNode } from "./flow-editor/FlowNode.tsx";
import { FlowEdge } from "./flow-editor/FlowEdge.tsx";
import { EdgePropertiesDialog } from "./flow-editor/EdgePropertiesDialog.tsx";
import { NodePropertiesDialog } from "./flow-editor/NodePropertiesDialog.tsx";
import { agentService } from "../../services/agentService.ts";
import type {
  Node,
  Edge,
  Point,
  DragState,
  ConnectionState,
} from "./flow-editor/types.ts";

interface PromptTreeEditorProps {
  onBack: () => void;
  agentId: string;
}

const PromptTreeEditor: React.FC<PromptTreeEditorProps> = ({
  onBack,
  agentId,
}) => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();

  // State
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<ConnectionState | null>(
    null
  );
  const [mousePosition, setMousePosition] = useState<Point>({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [showEdgeProperties, setShowEdgeProperties] = useState(false);
  const [showNodeProperties, setShowNodeProperties] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 });
  const [startingNodeId, setStartingNodeId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [llmId, setLlmId] = useState<string | null>(null);
  const [hasStates, setHasStates] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refs
  const editorRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const initialNodesRef = useRef<Node[]>([]);
  const initialEdgesRef = useRef<Edge[]>([]);
  const initialStartingNodeIdRef = useRef<string>("");

  // Default parameters structure for edges
  const defaultParameters = useMemo(
    () => ({
      type: "object",
      properties: {},
    }),
    [],
  );

  // Load LLM data and initialize the flow
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Get the agent to find the LLM ID
        const agent = await agentService.get(agentId);
        if (!agent || !agent.response_engine.llm_id) {
          throw new Error("Agent or LLM ID not found");
        }

        setLlmId(agent.response_engine.llm_id);

        // Get the LLM data
        const llm = await agentService.getLLMData(agent.response_engine.llm_id);

        // Only initialize the flow with states if they exist
        if (llm.states && llm.states.length > 0) {
          setHasStates(true);

          // Set the starting node ID from the LLM data
          if (llm.starting_state) {
            setStartingNodeId(llm.starting_state);
            initialStartingNodeIdRef.current = llm.starting_state;
          } else if (llm.states.length > 0) {
            // If no starting_state is specified, use the first state
            setStartingNodeId(llm.states[0].name);
            initialStartingNodeIdRef.current = llm.states[0].name;
          }

          // Create nodes from states
          // First, find the starting state
          const startingStateName = llm.starting_state || llm.states[0].name;
          const startingStateIndex = llm.states.findIndex(
            (state) => state.name === startingStateName
          );
          const nodeHeight = 140; // Approximate height of a node
          const verticalSpacing = nodeHeight * 1.5; // Half the previous spacing

          // Create nodes with vertical arrangement
          const stateNodes = llm.states.map((state, index) => {
            // Calculate position based on relationship to starting node
            let position;
            if (state.name === startingStateName) {
              // Starting node at the top center
              position = { x: 400, y: 100 };
            } else {
              // Position other nodes vertically below the starting node
              // Adjust index to account for the starting node being first
              let verticalIndex = index;
              if (index > startingStateIndex) {
                verticalIndex = index;
              } else {
                verticalIndex = index + 1;
              }

              // If this is the starting state, it's already positioned
              if (index === startingStateIndex) {
                verticalIndex = 0;
              } else {
                // Otherwise, adjust the index to skip the starting state's position
                verticalIndex = index >= startingStateIndex ? index : index + 1;
              }

              position = {
                x: 400,
                y: 100 + verticalIndex * verticalSpacing,
              };
            }

            return {
              id: state.name, // Ensure id matches state.name
              position,
              type: state.name === startingStateName ? "start" : "state",
              data: {
                state_name: state.name || "", // Ensure state_name matches state.name
                state_prompt: state.state_prompt || "",
                tools: state.tools || [],
                interruption_sensitivity:
                  state.interruption_sensitivity || false,
              },
            };
          });

          setNodes(stateNodes);
          initialNodesRef.current = JSON.parse(JSON.stringify(stateNodes));

          // Create edges from state transitions
          const stateEdges: Edge[] = [];
          llm.states.forEach((state) => {
            if (state.edges && state.edges.length > 0) {
              state.edges.forEach((edge, edgeIndex) => {
                // Ensure parameters has the correct structure
                const parameters = edge.parameters || defaultParameters;

                stateEdges.push({
                  id: `${state.name}-${edge.destination_state_name}-${edgeIndex}`,
                  from: state.name,
                  to: edge.destination_state_name,
                  fromPoint: "bottom",
                  toPoint: "top",
                  fromHandle: 0,
                  toHandle: 0,
                  label: edge.description,
                  data: {
                    parameters,
                  },
                });
              });
            }
          });

          setEdges(stateEdges);
          initialEdgesRef.current = JSON.parse(JSON.stringify(stateEdges));
        } else {
          // If no states exist, don't create any default nodes
          setHasStates(false);
          setNodes([]);
          setEdges([]);
          initialNodesRef.current = [];
          initialEdgesRef.current = [];
          initialStartingNodeIdRef.current = "";
        }

        // Initialize with no unsaved changes
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error loading LLM data:", error);
        toast({
          title: "Error loading data",
          description: "Could not load the prompt tree data",
          status: "error",
          duration: 3000,
        });

        // Set empty state if loading fails
        setHasStates(false);
        setNodes([]);
        setEdges([]);
        initialNodesRef.current = [];
        initialEdgesRef.current = [];
        initialStartingNodeIdRef.current = "";
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [agentId, toast, defaultParameters]);

  // Check for unsaved changes whenever nodes, edges, or startingNodeId changes
  useEffect(() => {
    if (isLoading) return;

    // Deep comparison of nodes and edges
    const nodesChanged =
      JSON.stringify(nodes) !== JSON.stringify(initialNodesRef.current);
    const edgesChanged =
      JSON.stringify(edges) !== JSON.stringify(initialEdgesRef.current);
    const startingNodeChanged =
      startingNodeId !== initialStartingNodeIdRef.current;

    setHasUnsavedChanges(nodesChanged || edgesChanged || startingNodeChanged);
  }, [nodes, edges, startingNodeId, isLoading]);

  // Track mouse position and handle panning
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });

      // Handle panning
      if (isPanning) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;

        setPan((prevPan) => ({
          x: prevPan.x + dx,
          y: prevPan.y + dy,
        }));

        setPanStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      if (isPanning) {
        setIsPanning(false);

        // Update cursor when releasing mouse button
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grab";
        }
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isPanning, panStart]);

  // Handle mouse wheel zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        // Scroll up -> zoom in
        handleZoomIn();
      } else {
        // Scroll down -> zoom out
        handleZoomOut();
      }
    };

    const editorElement = editorRef.current;
    if (editorElement) {
      editorElement.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, [scale, pan]);

  // Node creation
  const handleNewState = () => {
    const stateNumber = nodes.length + 1;
    const id = `new_state_${stateNumber}`;
    const newNode: Node = {
      id,
      position: { x: 100, y: 100 + nodes.length * 150 },
      title: `New State`,
      type: "state",
      data: {
        state_name: id, // Ensure state_name matches id
        state_prompt: "",
        tools: [],
      },
    };

    // If this is the first node, set it as the starting state
    if (nodes.length === 0) {
      newNode.type = "start";
      setStartingNodeId(id);
      setHasStates(true);
    }

    setNodes([...nodes, newNode]);
  };

  // Dragging
  const handleDragStart = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.stopPropagation(); // Prevent background panning when dragging nodes

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      setDragState({
        isDragging: true,
        start: {
          x: e.clientX - node.position.x,
          y: e.clientY - node.position.y,
        },
        current: { x: e.clientX, y: e.clientY },
      });
      setSelectedNode(nodeId);
      setSelectedEdge(null);
      setShowEdgeProperties(false);
    },
    [nodes]
  );

  const handleDrag = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      if (!dragState?.isDragging) return;

      const newX = e.clientX - dragState.start.x;
      const newY = e.clientY - dragState.start.y;

      setNodes((prev) =>
        prev.map((node) =>
          node.id === nodeId
            ? { ...node, position: { x: newX, y: newY } }
            : node
        )
      );
    },
    [dragState]
  );

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  const handleNodeClick = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.stopPropagation();
      if (!dragState) {
        // Only select if not dragging
        setSelectedNode(nodeId);
        setSelectedEdge(null);
        setShowEdgeProperties(false);
        setShowNodeProperties(true);
      }
    },
    [dragState]
  );

  // Node update
  const handleNodeUpdate = useCallback(
    (updatedNode: Node, originalNodeId: string) => {
      // Use the original node ID to find the node to update
      const nodeToUpdate = nodes.find((n) => n.id === originalNodeId);

      if (!nodeToUpdate) {
        console.error(`Node with ID ${originalNodeId} not found`);
        return;
      }

      // Check if the node ID (state name) has changed
      const idChanged = updatedNode.id !== originalNodeId;

      if (idChanged) {
        // Update edges if the node ID has changed
        setEdges((prev) =>
          prev.map((edge) => ({
            ...edge,
            from: edge.from === originalNodeId ? updatedNode.id : edge.from,
            to: edge.to === originalNodeId ? updatedNode.id : edge.to,
          }))
        );

        // If this was the starting node, update the starting node ID
        if (startingNodeId === originalNodeId) {
          setStartingNodeId(updatedNode.id);
        }
      }

      // Update the node in the nodes array
      setNodes((prev) =>
        prev.map((node) => (node.id === originalNodeId ? updatedNode : node))
      );
    },
    [nodes, startingNodeId, setEdges, setStartingNodeId]
  );

  // Node deletion
  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      // Delete all edges connected to this node
      setEdges((prev) =>
        prev.filter((edge) => edge.from !== nodeId && edge.to !== nodeId)
      );

      // Delete the node
      setNodes((prev) => prev.filter((node) => node.id !== nodeId));

      // If this was the starting node, set a new one if possible
      if (nodeId === startingNodeId && nodes.length > 1) {
        const newStartingNode = nodes.find((node) => node.id !== nodeId);
        if (newStartingNode) {
          setStartingNodeId(newStartingNode.id);
          setNodes((prev) =>
            prev.map((node) => ({
              ...node,
              type: node.id === newStartingNode.id ? "start" : "state",
            }))
          );
        }
      }

      // If we deleted the last node, set hasStates to false
      if (nodes.length <= 1) {
        setHasStates(false);
        setStartingNodeId("");
      }

      setSelectedNode(null);
      setShowNodeProperties(false);
    },
    [nodes, startingNodeId]
  );

  // Set starting node
  const handleSetAsStarting = useCallback((nodeId: string) => {
    setStartingNodeId(nodeId);

    // Update node types
    setNodes((prev) =>
      prev.map((node) => ({
        ...node,
        type: node.id === nodeId ? "start" : "state",
      }))
    );
  }, []);

  const handleBackgroundMouseDown = useCallback((e: React.MouseEvent) => {
    // If user clicks on a node or a connector, we do NOT want to start panning.
    const nodeCheck = (e.target as HTMLElement).closest("[data-node-id]");
    const connectorCheck = (e.target as HTMLElement).closest(
      "[data-connector]"
    );

    if (nodeCheck || connectorCheck) {
      return;
    }
    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY });

    if (canvasRef.current) {
      canvasRef.current.style.cursor = "grabbing";
    }
    // Close any open dialogs when clicking on background
    setSelectedNode(null);
    setSelectedEdge(null);
    setShowEdgeProperties(false);
    setShowNodeProperties(false);
  }, []);

  // Connection handling
  const handleConnectorMouseDown = useCallback(
    (
      nodeId: string,
      point: "top" | "bottom",
      handleIndex: number,
      e: React.MouseEvent
    ) => {
      e.stopPropagation();
      setConnectingFrom({ nodeId, point, handleIndex });
    },
    []
  );

  const handleEditorMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (connectingFrom) {
        // Find if we're over a connector
        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        let targetNodeId = null;
        let targetPoint = null;
        let targetHandleIndex = null;

        for (const el of elements) {
          if (el.hasAttribute("data-node-id")) {
            targetNodeId = el.getAttribute("data-node-id");
          }
          if (el.hasAttribute("data-connector")) {
            targetPoint = el.getAttribute("data-connector");
          }
          if (el.hasAttribute("data-handle-index")) {
            targetHandleIndex = Number.parseInt(
              el.getAttribute("data-handle-index") || "0",
              10
            );
          }
          if (targetNodeId && targetPoint && targetHandleIndex !== null) break;
        }

        if (
          targetNodeId &&
          targetPoint &&
          targetNodeId !== connectingFrom.nodeId
        ) {
          const newEdge: Edge = {
            id: `${connectingFrom.nodeId}-${targetNodeId}-${Date.now()}`,
            from: connectingFrom.nodeId,
            to: targetNodeId,
            fromPoint: connectingFrom.point,
            toPoint: targetPoint as "top" | "bottom",
            fromHandle: connectingFrom.handleIndex,
            toHandle: targetHandleIndex || 0,
            label:
              "The condition that triggers the transition to the next state. e.g., 'If the user has provided the name'",
            data: {
              parameters: defaultParameters,
            },
          };
          setEdges((prev) => [...prev, newEdge]);
        }

        setConnectingFrom(null);
      }
    },
    [connectingFrom, defaultParameters]
  );

  // Calculate connection line coordinates
  const getConnectionLineCoordinates = () => {
    if (!connectingFrom) return null;

    const sourceNode = nodes.find((n) => n.id === connectingFrom.nodeId);
    if (!sourceNode) return null;

    // Calculate base position
    const startX = sourceNode.position.x * scale + 135 * scale;

    const startY =
      sourceNode.position.y * scale +
      (connectingFrom.point === "bottom" ? 70 * scale : 0);

    return {
      startX,
      startY,
      endX: mousePosition.x - pan.x,
      endY: mousePosition.y,
    };
  };

  // Edge handling
  const handleEdgeClick = useCallback((edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setShowNodeProperties(false);
    setShowEdgeProperties(true);
  }, []);

  const handleEdgeUpdate = useCallback((updatedEdge: Edge) => {
    setEdges((prev) =>
      prev.map((edge) => (edge.id === updatedEdge.id ? updatedEdge : edge))
    );
    setSelectedEdge(updatedEdge);
  }, []);

  const handleEdgeDelete = useCallback((edgeId: string) => {
    setEdges((prev) => prev.filter((edge) => edge.id !== edgeId));
    setSelectedEdge(null);
    setShowEdgeProperties(false);
  }, []);

  // Zoom controls
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  // Save the prompt tree
  const savePromptTree = async () => {
    if (!llmId) {
      toast({
        title: "Error",
        description: "LLM ID not found",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);

    try {
      // Get the current LLM data
      const llm = await agentService.getRetellLLM(llmId);

      // Update the LLM with the new states (or empty array) and starting_state
      await agentService.updateRetellLLM(llmId, {
        agent_id: agentId,
        ...llm,
        states: nodes.map((node) => {
          // Find all edges that start from this node
          const nodeEdges = edges.filter((edge) => edge.from === node.id);

          // Convert edges to state edges format according to API docs
          const stateEdges = nodeEdges.map((edge) => ({
            destination_state_name: edge.to,
            description: edge.label || "Transition to next state",
            parameters: edge.data?.parameters || defaultParameters,
          }));

          // Create the state object according to API schema
          const stateObject = {
            name: node.id, // Use node.id as the state name
            state_prompt: node.data?.state_prompt || "",
            tools: node.data?.tools || [],
          };

          // Only add edges if there are any (API allows empty array or omission)
          if (stateEdges.length > 0) {
            stateObject.edges = stateEdges;
          }

          // Note: interruption_sensitivity is not part of the state schema according to API docs
          // It should be handled at the agent level, not state level

          return stateObject;
        }),
        starting_state: startingNodeId || null,
      });

      // Update the initial state references to match the current state
      initialNodesRef.current = JSON.parse(JSON.stringify(nodes));
      initialEdgesRef.current = JSON.parse(JSON.stringify(edges));
      initialStartingNodeIdRef.current = startingNodeId;

      // Reset unsaved changes flag
      setHasUnsavedChanges(false);

      toast({
        title: "Success",
        description: "Prompt tree saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving prompt tree:", error);
      toast({
        title: "Error",
        description: "Failed to save prompt tree",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderLoadingOverlay = () => {
    if (!isLoading) return null;

    return (
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex="20"
        bg={isDark ? "rgba(15, 18, 24, 0.7)" : "rgba(243, 244, 246, 0.7)"}
        justify="center"
        align="center"
      >
        <Spinner size="xl" />
      </Flex>
    );
  };

  // Empty state when no nodes exist
  const renderEmptyState = () => (
    <Flex
      direction="column"
      align="center"
      justify="center"
      h="100%"
      p={8}
      textAlign="center"
    >
      {/* Empty canvas with no text */}
    </Flex>
  );

  return (
    <Box
      flex={1}
      position="relative"
      bg={isDark ? "#0f1218" : "gray.100"}
      overflow="hidden"
    >
      {/* Editor canvas */}
      {hasStates ? (
        <Box
          ref={editorRef}
          position="absolute"
          inset={0}
          onMouseUp={handleEditorMouseUp}
          onMouseDown={handleBackgroundMouseDown}
          onClick={(e) => {
            // Close properties when clicking on the canvas
            if (
              e.target === e.currentTarget ||
              e.target === canvasRef.current
            ) {
              setSelectedNode(null);
              setSelectedEdge(null);
              setShowEdgeProperties(false);
              setShowNodeProperties(false);
            }
          }}
        >
          <div
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              cursor: "grab", // Default cursor for the canvas
              overflow: "hidden",
            }}
          >
            <div
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px)`,
                transformOrigin: "0 0",
                width: "100%",
                height: "100%",
                position: "absolute",
              }}
            >
              <svg
                width="100%"
                height="100%"
                style={{ position: "absolute", pointerEvents: "none" }}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="rgb(75, 85, 99)" />
                  </marker>
                </defs>

                {edges.map((edge) => {
                  const sourceNode = nodes.find((n) => n.id === edge.from);
                  const targetNode = nodes.find((n) => n.id === edge.to);
                  if (!sourceNode || !targetNode) return null;

                  return (
                    <FlowEdge
                      key={edge.id}
                      edge={edge}
                      sourcePosition={{
                        x: sourceNode.position.x * scale + 135 * scale, // Center of node
                        y:
                          sourceNode.position.y * scale +
                          (edge.fromPoint === "bottom" ? 70 * scale : 0),
                      }}
                      targetPosition={{
                        x: targetNode.position.x * scale + 135 * scale, // Center of node
                        y:
                          targetNode.position.y * scale +
                          (edge.toPoint === "bottom" ? 70 * scale : 0),
                      }}
                      onEdgeClick={handleEdgeClick}
                    />
                  );
                })}

                {connectingFrom &&
                  (() => {
                    const coords = getConnectionLineCoordinates();
                    if (!coords) return null;

                    return (
                      <path
                        d={`M ${coords.startX} ${coords.startY} L ${coords.endX} ${coords.endY}`}
                        stroke="rgb(75, 85, 99)"
                        strokeWidth={2}
                        fill="none"
                        strokeDasharray="4"
                        pointerEvents="none"
                      />
                    );
                  })()}
              </svg>

              {nodes.map((node) => (
                <div
                  key={node.id}
                  style={{
                    position: "absolute",
                    left: `${node.position.x * scale}px`,
                    top: `${node.position.y * scale}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                  }}
                  data-node-id={node.id}
                  onClick={(e) => handleNodeClick(e, node.id)}
                >
                  <FlowNode
                    node={{
                      ...node,
                      position: { x: 0, y: 0 }, // Position is handled by the parent div
                      type: node.id === startingNodeId ? "start" : "state",
                    }}
                    selected={selectedNode === node.id}
                    onDragStart={(e) => handleDragStart(e, node.id)}
                    onDrag={(e) => handleDrag(e, node.id)}
                    onDragEnd={handleDragEnd}
                    onConnectorMouseDown={handleConnectorMouseDown}
                  />
                </div>
              ))}
            </div>
          </div>
        </Box>
      ) : (
        renderEmptyState()
      )}

      {/* Render loading overlay */}
      {renderLoadingOverlay()}

      {/* Rest of the components */}

      {/* Node Properties Dialog */}
      {showNodeProperties &&
        selectedNode &&
        (() => {
          const node = nodes.find((n) => n.id === selectedNode);
          if (!node) return null;

          return (
            <NodePropertiesDialog
              node={node}
              onClose={() => setShowNodeProperties(false)}
              onUpdate={handleNodeUpdate}
              onDelete={handleNodeDelete}
              onSetAsStarting={handleSetAsStarting}
              isStartingNode={node.id === startingNodeId}
              agentId={agentId}
            />
          );
        })()}

      {/* Edge Properties Dialog */}
      {showEdgeProperties && selectedEdge && (
        <EdgePropertiesDialog
          edge={selectedEdge}
          onClose={() => setShowEdgeProperties(false)}
          onUpdate={handleEdgeUpdate}
          onDelete={handleEdgeDelete}
        />
      )}

      {/* Bottom controls - always show */}
      <Flex
        position="absolute"
        bottom="20px"
        left="50%"
        transform="translateX(-50%)"
        bg={isDark ? "gray.800" : "white"}
        borderRadius="md"
        border="1px solid"
        borderColor={isDark ? "gray.700" : "gray.200"}
        overflow="hidden"
        zIndex={10}
      >
        <Button
          leftIcon={<Plus size={16} />}
          bg="transparent"
          color={isDark ? "white" : "gray.800"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          borderRadius="0"
          borderRight="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          px={4}
          onClick={handleNewState}
        >
          New State
        </Button>
        <IconButton
          aria-label="Zoom in"
          icon={<Plus size={16} />}
          bg="transparent"
          color={isDark ? "white" : "gray.800"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          borderRadius="0"
          borderRight="1px solid"
          borderColor={isDark ? "gray.700" : "gray.200"}
          onClick={handleZoomIn}
        />
        <IconButton
          aria-label="Zoom out"
          icon={<Minus size={16} />}
          bg="transparent"
          color={isDark ? "white" : "gray.800"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
          borderRadius="0"
          onClick={handleZoomOut}
        />
      </Flex>

      {/* Save and Back buttons - positioned at the top left */}
      <Flex position="absolute" top="20px" left="20px" gap={2} zIndex={10}>
        <IconButton
          aria-label="Back"
          icon={<ArrowLeft />}
          variant="solid"
          colorScheme="gray"
          color={isDark ? "gray:100" : "gray.800"}
          onClick={onBack}
        />
        {hasUnsavedChanges && (
          <Button
            leftIcon={<Save size={16} />}
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.400" }}
            background={isDark ? "gray.700" : "gray.300"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            onClick={savePromptTree}
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            Save
          </Button>
        )}
      </Flex>

      {/* Zoom indicator - always show */}
      <Box
        position="absolute"
        bottom="20px"
        right="60px"
        bg={isDark ? "gray.800" : "white"}
        borderRadius="md"
        border="1px solid"
        borderColor={isDark ? "gray.700" : "gray.200"}
        px={2}
        py={1}
        zIndex={10}
        fontSize="sm"
        color={isDark ? "gray.300" : "gray.600"}
      >
        {Math.round(scale * 100)}%
      </Box>
    </Box>
  );
};

export default PromptTreeEditor;
